import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useUserRole } from "../../../context/UserRoleContext";
import PhotoUpload from "../components/management/PhotoUpload";
import PhotoUploadProgressOverlay from "../components/management/PhotoUploadProgressOverlay";
import BarWeeklyHoursSection from "../components/management/BarWeeklyHoursSection";
import BarTimeSlotsConfigSection, {
  DEFAULT_SHOW_SLOT_TEMPLATE,
} from "../components/management/BarTimeSlotsConfigSection";
import {
  createDefaultWeeklyHours,
  weeklyHoursToApiPayload,
} from "../../../utils/barWeeklyHours";

function photoItemsToUrls(items, newUrls) {
  let i = 0;
  return items.map((item) =>
    item.type === "url" ? item.url : newUrls[i++]
  );
}
import {
  GoogleMap,
  LoadScript,
  Marker,
  Autocomplete,
} from "@react-google-maps/api";
import { uploadPhotoFileWithGuards } from "../../../services/uploadPhoto";

const libraries = ["places"];
const mapContainerStyle = {
  width: "100%",
  height: "400px",
  borderRadius: "8px",
  marginTop: "10px",
};

const defaultCenter = {
  lat: 24.5551, // Key West, FL
  lng: -81.78,
};

const Onboarding = () => {
  const navigate = useNavigate();
  const { userData } = useUserRole();
  const [barsCreated, setBarsCreated] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [autocomplete, setAutocomplete] = useState(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [markerPosition, setMarkerPosition] = useState(null);
  const [mapZoom, setMapZoom] = useState(13); // Start zoomed out, zoom in when address selected
  const [timeSlotErrors, setTimeSlotErrors] = useState({});
  const [barHoursError, setBarHoursError] = useState("");
  const [formError, setFormError] = useState("");
  const [uploadOverlay, setUploadOverlay] = useState({
    visible: false,
    total: 0,
    current: -1,
    results: [],
  });
  const [rejectedOutdoorIndices, setRejectedOutdoorIndices] = useState([]);
  const [rejectedIndoorIndices, setRejectedIndoorIndices] = useState([]);
  const uploadAbortRef = useRef(null);
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    latitude: null,
    longitude: null,
    weekly_hours: createDefaultWeeklyHours(),
    indoor_photos: [],
    outdoor_photos: [],
    time_slots_v2: {
      version: 2,
      enabled: true,
      defaultSlots: DEFAULT_SHOW_SLOT_TEMPLATE(),
      byDay: {},
    },
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const onLoadAutocomplete = (autocompleteObj) => {
    setAutocomplete(autocompleteObj);
  };

  const onPlaceChanged = () => {
    if (autocomplete !== null) {
      const place = autocomplete.getPlace();

      if (place.geometry) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();

        setFormData({
          ...formData,
          address: place.formatted_address,
          latitude: lat,
          longitude: lng,
        });

        setMapCenter({ lat, lng });
        setMarkerPosition({ lat, lng });
        setMapZoom(18); // Zoom in close when address is selected
      }
    }
  };

  const onMarkerDragEnd = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();

    setFormData({
      ...formData,
      latitude: lat,
      longitude: lng,
    });

    setMarkerPosition({ lat, lng });
    // Note: We intentionally don't update the address when marker is dragged
    // This allows fine-tuning marker position without changing the business address
  };

  const handleIndoorPhotosChange = (items) => {
    setRejectedIndoorIndices([]);
    setFormData({ ...formData, indoor_photos: items });
  };

  const handleOutdoorPhotosChange = (items) => {
    setRejectedOutdoorIndices([]);
    setFormData({ ...formData, outdoor_photos: items });
  };

  const handleRemoveOutdoor = (index) => {
    if (formData.outdoor_photos.length <= 1) {
      setFormError(
        "You need at least one outdoor photo. Add a new one first, then you can remove this one."
      );
      return;
    }
    const updated = formData.outdoor_photos.filter((_, i) => i !== index);
    setRejectedOutdoorIndices((prev) =>
      prev.filter((i) => i !== index).map((i) => (i > index ? i - 1 : i))
    );
    setFormData({ ...formData, outdoor_photos: updated });
    setFormError("");
  };

  const handleRemoveIndoor = (index) => {
    const updated = formData.indoor_photos.filter((_, i) => i !== index);
    setRejectedIndoorIndices((prev) =>
      prev.filter((i) => i !== index).map((i) => (i > index ? i - 1 : i))
    );
    setFormData({ ...formData, indoor_photos: updated });
    setFormError("");
  };

  const refreshUserData = async () => {
    try {
      // Force a reload of user data to get updated has_completed_setup
      const response = await fetch("/api/auth/user", {
        credentials: "include",
      });
      const data = await response.json();
      return data.success;
    } catch (error) {
      console.error("Error refreshing user data:", error);
      return false;
    }
  };

  const handleAddBar = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!formData.name.trim()) {
      setFormError("Please enter a bar name.");
      return;
    }

    if (!formData.address.trim()) {
      setFormError("Please enter an address.");
      return;
    }

    if (!formData.latitude || !formData.longitude) {
      setFormError(
        "Please select a valid address from the dropdown suggestions to set location."
      );
      return;
    }

    if (barHoursError) {
      setFormError(barHoursError);
      return;
    }

    const outdoorCount = formData.outdoor_photos.length;
    if (outdoorCount < 1) {
      setFormError("Please add at least one outdoor photo.");
      return;
    }

    const outdoorFiles = formData.outdoor_photos.filter((i) => i.type === "file");
    const indoorFiles = formData.indoor_photos.filter((i) => i.type === "file");
    const totalFiles = outdoorFiles.length + indoorFiles.length;

    let finalOutdoorUrls = [];
    let finalIndoorUrls = [];

    if (totalFiles > 0) {
      uploadAbortRef.current = new AbortController();
      setUploadOverlay({
        visible: true,
        total: totalFiles,
        current: -1,
        results: [],
      });
      const results = [];
      const outdoorUrls = [];
      const indoorUrls = [];

      try {
        for (let i = 0; i < outdoorFiles.length; i++) {
          let data;
          try {
            data = await uploadPhotoFileWithGuards(outdoorFiles[i].file, {
              batchSignal: uploadAbortRef.current.signal,
            });
          } catch (uploadErr) {
            if (uploadErr.code === "CANCELLED") {
              setFormError("Upload cancelled.");
            } else if (uploadErr.code === "TIMEOUT") {
              setFormError(uploadErr.message);
            } else {
              setFormError(
                uploadErr.message ||
                  "Something went wrong uploading photos. Please try again."
              );
            }
            return;
          }
          const ok = data.ok && data.url;
          results.push(ok ? "success" : "rejected");
          if (ok) outdoorUrls.push(data.url);
          setUploadOverlay((prev) => ({
            ...prev,
            current: i,
            results: [...results],
          }));
        }
        for (let i = 0; i < indoorFiles.length; i++) {
          let data;
          try {
            data = await uploadPhotoFileWithGuards(indoorFiles[i].file, {
              batchSignal: uploadAbortRef.current.signal,
            });
          } catch (uploadErr) {
            if (uploadErr.code === "CANCELLED") {
              setFormError("Upload cancelled.");
            } else if (uploadErr.code === "TIMEOUT") {
              setFormError(uploadErr.message);
            } else {
              setFormError(
                uploadErr.message ||
                  "Something went wrong uploading photos. Please try again."
              );
            }
            return;
          }
          const ok = data.ok && data.url;
          results.push(ok ? "success" : "rejected");
          if (ok) indoorUrls.push(data.url);
          setUploadOverlay((prev) => ({
            ...prev,
            current: outdoorFiles.length + i,
            results: [...results],
          }));
        }
      } finally {
        setUploadOverlay((prev) => ({ ...prev, visible: false }));
        uploadAbortRef.current = null;
      }

      const rejectedOneBased = results
        .map((r, i) => (r === "rejected" ? i + 1 : null))
        .filter((x) => x != null);
      if (rejectedOneBased.length > 0) {
        const outdoorFormIndices = [];
        let fi = 0;
        let outdoorUrlIdx = 0;
        let indoorUrlIdx = 0;
        const newOutdoorPhotos = formData.outdoor_photos.map((item) => {
          if (item.type === "url") return item;
          const r = results[fi++];
          if (r === "success") {
            if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
            return { type: "url", url: outdoorUrls[outdoorUrlIdx++] };
          }
          return item;
        });
        const newIndoorPhotos = formData.indoor_photos.map((item) => {
          if (item.type === "url") return item;
          const r = results[fi++];
          if (r === "success") {
            if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
            return { type: "url", url: indoorUrls[indoorUrlIdx++] };
          }
          return item;
        });
        setFormData({
          ...formData,
          outdoor_photos: newOutdoorPhotos,
          indoor_photos: newIndoorPhotos,
        });
        fi = 0;
        for (let i = 0; i < formData.outdoor_photos.length; i++) {
          if (formData.outdoor_photos[i].type === "file") {
            if (results[fi] === "rejected") outdoorFormIndices.push(i);
            fi++;
          }
        }
        const indoorFormIndices = [];
        for (let i = 0; i < formData.indoor_photos.length; i++) {
          if (formData.indoor_photos[i].type === "file") {
            if (results[fi] === "rejected") indoorFormIndices.push(i);
            fi++;
          }
        }
        setRejectedOutdoorIndices(outdoorFormIndices);
        setRejectedIndoorIndices(indoorFormIndices);
        setFormError(
          `Photo${rejectedOneBased.length > 1 ? "s" : ""} ${rejectedOneBased.join(" and ")} ${rejectedOneBased.length > 1 ? "were" : "was"} rejected. Please remove or replace ${rejectedOneBased.length > 1 ? "them" : "it"}.`
        );
        return;
      }

      finalOutdoorUrls = photoItemsToUrls(formData.outdoor_photos, outdoorUrls);
      finalIndoorUrls = photoItemsToUrls(formData.indoor_photos, indoorUrls);
    } else {
      finalOutdoorUrls = formData.outdoor_photos.map((i) => i.url);
      finalIndoorUrls = formData.indoor_photos.map((i) => i.url);
    }

    const v2 = formData.time_slots_v2;
    if (v2?.enabled) {
      const checkList = (arr, label) => {
        if (!Array.isArray(arr)) return null;
        for (let i = 0; i < arr.length; i++) {
          const s = arr[i];
          if (!s.name?.trim() || !s.start_time || !s.end_time) {
            return `Please complete all ${label} time slot fields (name, start, end).`;
          }
        }
        return null;
      };
      const defErr = checkList(v2.defaultSlots, "default");
      if (defErr) {
        setFormError(defErr);
        return;
      }
      const by = v2.byDay || {};
      for (const k of Object.keys(by)) {
        const list = by[k];
        if (Array.isArray(list) && list.length > 0) {
          const e = checkList(list, "per-day");
          if (e) {
            setFormError(e);
            return;
          }
        }
      }
    }

    const hasTimeSlotErrors = Object.values(timeSlotErrors).some(
      (err) => err != null && err !== ""
    );
    if (hasTimeSlotErrors) {
      setFormError("Please fix time slot validation errors before submitting.");
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare bar data
      const barData = {
        name: formData.name,
        address: formData.address,
        latitude: formData.latitude,
        longitude: formData.longitude,
        weekly_hours: weeklyHoursToApiPayload(formData.weekly_hours),
        indoor_photos: { photos: finalIndoorUrls },
        outdoor_photos: { photos: finalOutdoorUrls },
        time_slots: formData.time_slots_v2,
      };

      const response = await fetch("/api/bars", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(barData),
      });

      const data = await response.json();

      if (data.success) {
        setBarsCreated([...barsCreated, data.bar]);

        // Reset form for next bar
        setFormData({
          name: "",
          address: "",
          latitude: null,
          longitude: null,
          weekly_hours: createDefaultWeeklyHours(),
          indoor_photos: [],
          outdoor_photos: [],
          time_slots_v2: {
            version: 2,
            enabled: true,
            defaultSlots: DEFAULT_SHOW_SLOT_TEMPLATE(),
            byDay: {},
          },
        });

        setMarkerPosition(null);
        setMapCenter(defaultCenter);
        setMapZoom(13); // Reset to zoomed out view

        // Refresh user data to update has_completed_setup
        await refreshUserData();

        // Show modal asking what to do next
        setShowModal(true);
      } else {
        setFormError(data.error || "Something went wrong. Please try again.");
      }
    } catch (error) {
      console.error("Add bar error:", error);
      setFormError("Failed to add bar. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContinueToDashboard = async () => {
    // Ensure user data is refreshed before navigating
    await refreshUserData();

    // Small delay to ensure context updates
    setTimeout(() => {
      window.location.href = "/bar-owner/dashboard";
    }, 100);
  };

  const handleAddAnother = () => {
    setShowModal(false);
    // Form is already reset, user can continue
  };

  const handleModalContinue = async () => {
    setShowModal(false);
    await handleContinueToDashboard();
  };

  return (
    <LoadScript
      googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ""}
      libraries={libraries}
    >
      <div className="onboarding-page">
        <PhotoUploadProgressOverlay
          visible={uploadOverlay.visible}
          total={uploadOverlay.total}
          current={uploadOverlay.current}
          results={uploadOverlay.results}
          onCancel={
            uploadOverlay.visible
              ? () => uploadAbortRef.current?.abort()
              : undefined
          }
        />
        {/* Modal */}
        {showModal && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>Bar Added Successfully! 🎉</h2>
              <p>What would you like to do next?</p>
              <div className="modal-actions">
                <button
                  className="modal-btn secondary"
                  onClick={handleAddAnother}
                >
                  Add Another Bar
                </button>
                <button
                  className="modal-btn primary"
                  onClick={handleModalContinue}
                >
                  Continue to Dashboard
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="onboarding-container">
          <div className="onboarding-header">
            <h1>Welcome to Local Live Music! 🎵</h1>
            <p>Let's set up your first bar to get started</p>
            {barsCreated.length > 0 && (
              <div className="bars-created-count">
                {barsCreated.length} {barsCreated.length === 1 ? "bar" : "bars"}{" "}
                created
              </div>
            )}
          </div>

          <form onSubmit={handleAddBar} className="onboarding-form">
            <div className="onboarding-section">
              <h2 className="section-title">Bar Information</h2>

              <div className="form-grid">
                <div className="input-group full-width">
                  <label className="input-label">Bar Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="settings-input"
                    placeholder="Enter bar name"
                    required
                    maxLength={128}
                  />
                </div>

                <div className="input-group full-width">
                  <label className="input-label">Address *</label>
                  <Autocomplete
                    onLoad={onLoadAutocomplete}
                    onPlaceChanged={onPlaceChanged}
                  >
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className="settings-input"
                      placeholder="Start typing address..."
                      required
                      maxLength={255}
                    />
                  </Autocomplete>
                  <p className="input-hint">
                    Select from dropdown suggestions, then adjust the map marker
                    if needed
                  </p>
                </div>

                {/* Map */}
                <div className="input-group full-width">
                  <label className="input-label">
                    {formData.latitude && formData.longitude
                      ? "Finalize Location (Drag Marker)"
                      : "Select an address to place marker"}
                  </label>
                  <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    center={mapCenter}
                    zoom={mapZoom}
                    options={{ gestureHandling: "cooperative" }}
                  >
                    {formData.latitude && formData.longitude && (
                      <Marker
                        position={
                          markerPosition || {
                            lat: formData.latitude,
                            lng: formData.longitude,
                          }
                        }
                        draggable={true}
                        onDragEnd={onMarkerDragEnd}
                      />
                    )}
                  </GoogleMap>
                  {formData.latitude && formData.longitude && (
                    <p className="input-hint">
                      Coordinates: {formData.latitude.toFixed(6)},{" "}
                      {formData.longitude.toFixed(6)}
                    </p>
                  )}
                </div>

                <div className="input-group full-width">
                  <BarWeeklyHoursSection
                    value={formData.weekly_hours}
                    onChange={(next) =>
                      setFormData({ ...formData, weekly_hours: next })
                    }
                    disabled={isSubmitting}
                    onValidationChange={setBarHoursError}
                  />
                </div>
              </div>
            </div>

            <div className="onboarding-section">
              <h2 className="section-title">Bar Photos</h2>
              <p className="section-description">
                Add at least one outdoor photo (primary). You can add up to 3
                outdoor and 3 indoor photos.
              </p>

              <PhotoUpload
                label="Outdoor Photos"
                value={formData.outdoor_photos}
                onChange={handleOutdoorPhotosChange}
                onRemove={handleRemoveOutdoor}
                minPhotos={1}
                maxPhotos={3}
                rejectedIndices={rejectedOutdoorIndices}
              />

              <PhotoUpload
                label="Indoor Photos"
                value={formData.indoor_photos}
                onChange={handleIndoorPhotosChange}
                onRemove={handleRemoveIndoor}
                minPhotos={0}
                maxPhotos={3}
                rejectedIndices={rejectedIndoorIndices}
              />
            </div>

            <div className="onboarding-section">
              <h2 className="section-title">Show Time Slots</h2>
              <p className="section-description">
                Optional named show times for booking. You can use one default
                set for the week or override specific weekdays.
              </p>

              <BarTimeSlotsConfigSection
                value={formData.time_slots_v2}
                onChange={(time_slots_v2) =>
                  setFormData({ ...formData, time_slots_v2 })
                }
                disabled={isSubmitting}
                onValidationChange={setTimeSlotErrors}
              />
            </div>

            {formError && (
              <div className="error-message" role="alert">
                <p>{formError}</p>
              </div>
            )}
            <div className="onboarding-actions">
              <button
                type="submit"
                className="submit-btn"
                disabled={isSubmitting || !formData.latitude}
              >
                {isSubmitting
                  ? "Adding Bar..."
                  : barsCreated.length > 0
                  ? "Add Another Bar"
                  : "Add Bar"}
              </button>
            </div>
          </form>

          {barsCreated.length > 0 && (
            <div className="bars-created-list">
              <h3>Bars Created:</h3>
              {barsCreated.map((bar, index) => (
                <div key={bar.bar_id} className="bar-created-item">
                  ✓ {bar.name}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </LoadScript>
  );
};

export default Onboarding;
